/**
 * Loop operacional mínimo: exceção → contexto → problema → ação → Mesa.
 * Contexto de navegação viaja na URL. A origem persistida fica no metadata do problema.
 * Nada aqui grava registro.
 */

export const EXCEPTION_TYPES = [
  'bottleneck',
  'sla_breach',
  'overload',
  'andon',
  'critical_problem',
  'delayed_task',
  'delayed_project',
] as const;

export type ExceptionType = (typeof EXCEPTION_TYPES)[number];

/**
 * Andon ainda aberto. escalated_manager é a segunda onda do mesmo estouro
 * (24h depois de andon_triggered), não um estado encerrado.
 * Contador, lista e clique do Painel usam exatamente este conjunto.
 */
export const ANDON_PANEL_STATUSES = ['andon_triggered', 'escalated_manager', 'critical_director'] as const;

export type AndonPanelStatus = (typeof ANDON_PANEL_STATUSES)[number];

export interface OperationalOrigin {
  exception_type: ExceptionType;
  label: string;
  stage_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  workflow_id?: string | null;
  execution_id?: string | null;
  task_id?: string | null;
  project_id?: string | null;
  action_id?: string | null;
  period_label?: string | null;
  wip?: number | null;
  avg_minutes?: number | null;
  signal_label?: string | null;
  detected_at: string;
  return_path: string;
  /** Organização em que o sinal foi registrado. Não valida sozinha o objeto apontado. */
  organization_id?: string | null;
}

export interface ProblemCreateDraft {
  title: string;
  description: string;
  source: string;
  department: string | null;
  step: string | null;
  workflow_id: string | null;
  metadata: {
    operational_origin: OperationalOrigin;
  };
}

export type DeskKind = 'operacional' | 'gestao';

export interface DeskPriorityView {
  managerialLabel: string;
  operationalSignal: string | null;
}

const MANAGERIAL_PRIORITY_LABELS: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
  urgentissima: 'Urgente',
};

export function isExceptionType(value: string | null | undefined): value is ExceptionType {
  return EXCEPTION_TYPES.includes(value as ExceptionType);
}

export function countsAsOpenAndon(slaStatus: string | null | undefined): boolean {
  return ANDON_PANEL_STATUSES.includes(slaStatus as AndonPanelStatus);
}

export function openAndonCount(counts: {
  andonTriggered?: number;
  escalatedManager?: number;
  criticalDirector?: number;
}): number {
  return (counts.andonTriggered ?? 0) + (counts.escalatedManager ?? 0) + (counts.criticalDirector ?? 0);
}

export function bottleneckFocusPath(input: {
  stageName: string;
  departmentId?: string | null;
  wip?: number | null;
  avgMinutes?: number | null;
}): string {
  const params = new URLSearchParams();
  params.set('tab', 'flow');
  params.set('view', 'bottlenecks');
  params.set('stage', input.stageName);
  if (input.departmentId) params.set('dept', input.departmentId);
  if (input.wip != null) params.set('wip', String(input.wip));
  if (input.avgMinutes != null) params.set('minutes', String(input.avgMinutes));
  return `/centro-operacional?${params.toString()}`;
}

export function readFocusStage(search: string): string | null {
  const stage = new URLSearchParams(search).get('stage');
  const trimmed = stage?.trim();
  return trimmed ? trimmed : null;
}

export function stageIsFocused(stageName: string, focusStage: string | null | undefined): boolean {
  if (!focusStage) return false;
  return stageName.trim().toLowerCase() === focusStage.trim().toLowerCase();
}

export function buildBottleneckOrigin(input: {
  stageName: string;
  departmentId?: string | null;
  departmentName?: string | null;
  workflowId?: string | null;
  wip?: number | null;
  avgMinutes?: number | null;
  periodLabel?: string | null;
  detectedAt?: string;
}): OperationalOrigin {
  const detectedAt = input.detectedAt ?? new Date().toISOString();
  return {
    exception_type: 'bottleneck',
    label: `Painel → Fluxo → Gargalo → Etapa ${input.stageName}`,
    stage_name: input.stageName,
    department_id: input.departmentId ?? null,
    department_name: input.departmentName ?? null,
    workflow_id: input.workflowId ?? null,
    wip: input.wip ?? null,
    avg_minutes: input.avgMinutes ?? null,
    period_label: input.periodLabel ?? null,
    signal_label: 'Gargalo',
    detected_at: detectedAt,
    return_path: bottleneckFocusPath({
      stageName: input.stageName,
      departmentId: input.departmentId,
      wip: input.wip,
      avgMinutes: input.avgMinutes,
    }),
  };
}

export function buildOverloadOrigin(input: {
  userId?: string | null;
  userName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  detectedAt?: string;
}): OperationalOrigin {
  const who = input.userName?.trim() || 'operador em sobrecarga';
  const detectedAt = input.detectedAt ?? new Date().toISOString();
  const params = new URLSearchParams();
  params.set('tab', 'capacity');
  params.set('view', 'team');
  if (input.userId) params.set('operator', input.userId);
  if (input.departmentId) params.set('dept', input.departmentId);
  return {
    exception_type: 'overload',
    label: `Painel → Capacidade → Equipe → ${who}`,
    department_id: input.departmentId ?? null,
    department_name: input.departmentName ?? null,
    signal_label: 'Sobrecarga',
    detected_at: detectedAt,
    return_path: `/centro-operacional?${params.toString()}`,
  };
}

export function buildAndonOrigin(input: {
  actionId: string;
  actionTitle: string;
  slaStatus: string;
  detectedAt?: string;
}): OperationalOrigin {
  const detectedAt = input.detectedAt ?? new Date().toISOString();
  const signal = input.slaStatus === 'critical_director'
    ? 'Escalação crítica'
    : input.slaStatus === 'escalated_manager'
      ? 'Escalado ao gerente'
      : 'Andon';
  return {
    exception_type: 'andon',
    label: `Painel → Ao Vivo → ${signal} → ${input.actionTitle}`,
    action_id: input.actionId,
    signal_label: signal,
    detected_at: detectedAt,
    return_path: `/acoes?actionId=${encodeURIComponent(input.actionId)}`,
  };
}

export function buildExecutionProblemOrigin(input: {
  executionId?: string | null;
  taskId?: string | null;
  title: string;
  detectedAt?: string;
}): OperationalOrigin {
  const detectedAt = input.detectedAt ?? new Date().toISOString();
  const params = new URLSearchParams();
  if (input.executionId) params.set('execution', input.executionId);
  if (input.taskId) params.set('task', input.taskId);
  const query = params.toString();
  return {
    exception_type: input.taskId ? 'delayed_task' : 'sla_breach',
    label: `Painel → Execução → ${input.title}`,
    execution_id: input.executionId ?? null,
    task_id: input.taskId ?? null,
    signal_label: input.taskId ? 'Tarefa' : 'SLA',
    detected_at: detectedAt,
    return_path: input.executionId
      ? `/execucoes/instancias/${encodeURIComponent(input.executionId)}`
      : `/centro-operacional${query ? `?${query}` : ''}`,
  };
}

function appendContextLines(origin: OperationalOrigin): string[] {
  const lines = [
    `Origem: ${origin.label}`,
    `Sinal operacional: ${origin.signal_label ?? origin.exception_type}`,
    `Detectado em: ${origin.detected_at}`,
  ];
  if (origin.stage_name) lines.push(`Etapa: ${origin.stage_name}`);
  if (origin.department_name) lines.push(`Departamento: ${origin.department_name}`);
  if (origin.wip != null) lines.push(`WIP: ${origin.wip}`);
  if (origin.avg_minutes != null) lines.push(`Tempo parado médio: ${origin.avg_minutes} min`);
  if (origin.period_label) lines.push(`Período: ${origin.period_label}`);
  if (origin.workflow_id) lines.push(`Workflow: ${origin.workflow_id}`);
  if (origin.execution_id) lines.push(`Execução: ${origin.execution_id}`);
  if (origin.task_id) lines.push(`Tarefa: ${origin.task_id}`);
  if (origin.action_id) lines.push(`Ação relacionada: ${origin.action_id}`);
  return lines;
}

export function buildProblemDraft(origin: OperationalOrigin): ProblemCreateDraft {
  const title = origin.exception_type === 'bottleneck' && origin.stage_name
    ? `Gargalo em ${origin.stage_name}`
    : origin.exception_type === 'overload'
      ? `Sobrecarga — ${origin.label.split('→').pop()?.trim() || 'equipe'}`
      : origin.exception_type === 'andon'
        ? `Andon — ${origin.label.split('→').pop()?.trim() || 'ocorrência'}`
        : `Exceção — ${origin.label.split('→').pop()?.trim() || 'operação'}`;

  return {
    title,
    description: appendContextLines(origin).join('\n'),
    source: `painel:${origin.exception_type}`,
    department: origin.department_name ?? null,
    step: origin.stage_name ?? null,
    workflow_id: origin.workflow_id ?? null,
    metadata: { operational_origin: origin },
  };
}

export function problemDraftPath(origin: OperationalOrigin): string {
  const params = new URLSearchParams();
  params.set('draft', '1');
  params.set('exception_type', origin.exception_type);
  params.set('origin_label', origin.label);
  params.set('return_path', origin.return_path);
  params.set('detected_at', origin.detected_at);
  if (origin.signal_label) params.set('signal_label', origin.signal_label);
  if (origin.stage_name) params.set('stage', origin.stage_name);
  if (origin.department_id) params.set('department_id', origin.department_id);
  if (origin.department_name) params.set('department_name', origin.department_name);
  if (origin.workflow_id) params.set('workflow_id', origin.workflow_id);
  if (origin.execution_id) params.set('execution_id', origin.execution_id);
  if (origin.task_id) params.set('task_id', origin.task_id);
  if (origin.project_id) params.set('project_id', origin.project_id);
  if (origin.organization_id) params.set('organization_id', origin.organization_id);
  if (origin.action_id) params.set('action_id', origin.action_id);
  if (origin.period_label) params.set('period_label', origin.period_label);
  if (origin.wip != null) params.set('wip', String(origin.wip));
  if (origin.avg_minutes != null) params.set('minutes', String(origin.avg_minutes));
  return `/problemas?${params.toString()}`;
}

function numParam(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (raw == null || raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function parseProblemDraft(search: string): OperationalOrigin | null {
  const query = search.includes('?') ? search.slice(search.indexOf('?') + 1) : search.replace(/^\?/, '');
  const params = new URLSearchParams(query);
  if (params.get('draft') !== '1') return null;
  const exceptionType = params.get('exception_type');
  if (!isExceptionType(exceptionType)) return null;
  const label = params.get('origin_label')?.trim();
  const returnPath = params.get('return_path')?.trim();
  const detectedAt = params.get('detected_at')?.trim();
  if (!label || !returnPath || !detectedAt) return null;
  return {
    exception_type: exceptionType,
    label,
    return_path: returnPath,
    detected_at: detectedAt,
    signal_label: params.get('signal_label'),
    stage_name: params.get('stage'),
    department_id: params.get('department_id'),
    department_name: params.get('department_name'),
    workflow_id: params.get('workflow_id'),
    execution_id: params.get('execution_id'),
    task_id: params.get('task_id'),
    project_id: params.get('project_id'),
    organization_id: params.get('organization_id'),
    action_id: params.get('action_id'),
    period_label: params.get('period_label'),
    wip: numParam(params, 'wip'),
    avg_minutes: numParam(params, 'minutes'),
  };
}

export function readOperationalOrigin(metadata: unknown): OperationalOrigin | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const raw = (metadata as { operational_origin?: unknown }).operational_origin;
  if (!raw || typeof raw !== 'object') return null;
  const origin = raw as Partial<OperationalOrigin>;
  if (!isExceptionType(origin.exception_type ?? null)) return null;
  if (!origin.label || !origin.return_path || !origin.detected_at) return null;
  return origin as OperationalOrigin;
}

export interface LinkedActionFields {
  problem_id?: string;
  priority: string;
  metadata?: Record<string, unknown>;
}

/** Prioridade informada permanece. O sinal da exceção, se existir, fica ao lado. */
export function buildLinkedActionFields(input: {
  problemId?: string | null;
  priority: string;
  operationalSignal?: string | null;
}): LinkedActionFields {
  const priority = input.priority;
  if (!input.problemId) {
    return {
      priority,
      ...(input.operationalSignal
        ? { metadata: { managerial_priority: priority, operational_signal: input.operationalSignal } }
        : {}),
    };
  }
  return {
    problem_id: input.problemId,
    priority,
    metadata: {
      problem_id: input.problemId,
      managerial_priority: priority,
      ...(input.operationalSignal ? { operational_signal: input.operationalSignal } : {}),
    },
  };
}

export function actionVisibleOnDesk(input: {
  assignedTo: string | null | undefined;
  viewerId: string | null | undefined;
  status: string | null | undefined;
}): boolean {
  if (!input.viewerId || !input.assignedTo) return false;
  if (input.assignedTo !== input.viewerId) return false;
  return isActiveDeskStatus(input.status);
}

export function isActiveDeskStatus(status: string | null | undefined): boolean {
  return status !== 'completed' && status !== 'cancelled' && status !== 'closed';
}

export function deskPriorityView(
  managerialPriority: string | null | undefined,
  operationalSignal: string | null | undefined,
): DeskPriorityView {
  const key = (managerialPriority ?? '').trim().toLowerCase();
  return {
    managerialLabel: MANAGERIAL_PRIORITY_LABELS[key] ?? (managerialPriority?.trim() || '—'),
    operationalSignal: operationalSignal?.trim() || null,
  };
}

export function classifyDeskItem(source: string | null | undefined): DeskKind {
  if (source === 'action') return 'gestao';
  return 'operacional';
}

/** Abrir o Painel, o Fluxo ou o formulário de rascunho não grava problema. */
export function navigationCreatesProblem(_search: string): boolean {
  return false;
}
